import React, { useState } from "react";
import axios from "axios";
import {
    Mail,
    CheckCircle2,
    AlertCircle,
    Loader2,
    Send,
    MessageSquare,
    Shield,
    Clock,
} from "lucide-react";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { SEO, breadcrumbSchema } from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NousContacter = () => {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        subject: "general",
        message: "",
        consent: false,
    });
    const [status, setStatus] = useState("idle"); // idle | loading | success | error
    const [responseMsg, setResponseMsg] = useState("");
    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
        // Clear specific error on typing
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: "" }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) {
            newErrors.name = "Le nom est obligatoire.";
        }
        if (!formData.email.trim()) {
            newErrors.email = "L'adresse e-mail est obligatoire.";
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = "L'adresse e-mail n'est pas valide.";
        }
        if (!formData.message.trim()) {
            newErrors.message = "Le message est obligatoire.";
        } else if (formData.message.trim().length < 10) {
            newErrors.message = "Le message doit contenir au moins 10 caractères.";
        }
        if (!formData.consent) {
            newErrors.consent = "Vous devez accepter le traitement de vos données.";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setStatus("loading");
        setResponseMsg("");

        try {
            const payload = {
                name: formData.name.trim(),
                email: formData.email.trim(),
                subject: formData.subject,
                message: formData.message.trim(),
            };
            const { data } = await axios.post(`${API}/contact`, payload);
            setStatus("success");
            setResponseMsg(data?.message || "Votre message a bien été envoyé !");
            setFormData({
                name: "",
                email: "",
                subject: "general",
                message: "",
                consent: false,
            });
        } catch (err) {
            setStatus("error");
            const detail = err?.response?.data?.detail;
            setResponseMsg(
                typeof detail === "string"
                    ? detail
                    : "Une erreur est survenue lors de l'envoi de votre message. Veuillez réessayer."
            );
        }
    };

    return (
        <div data-testid="contact-page">
            <SEO
                title="Nous contacter | Pronospot.com"
                description="Contactez l'équipe de Pronospot.com pour toute question, suggestion ou demande de partenariat. Formulaire de contact sécurisé et rapide."
                jsonLd={breadcrumbSchema([
                    { name: "Accueil", url: "https://pronospot.com/" },
                    { name: "Nous contacter", url: "https://pronospot.com/nous-contacter" },
                ])}
            />

            <section className="container-x pt-10 pb-6">
                <Breadcrumbs items={[{ label: "Nous contacter" }]} />
                <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white tracking-tighter">
                    Nous contacter
                </h1>
                <p className="text-gray-400 mt-4 max-w-2xl text-lg">
                    Une question, une suggestion de partenariat ou un problème technique ?
                    Remplissez le formulaire ci-dessous et notre équipe vous répondra sous 24h à 48h.
                </p>
            </section>

            <section className="container-x pb-20">
                <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-10">
                    {/* Contact details & info */}
                    <aside className="space-y-6">
                        <div className="card-elev p-6 space-y-6 bg-gradient-to-b from-[#162236]/80 to-[#0F172A]/80 border border-[#2A4A6B]">
                            <div className="flex items-start gap-4">
                                <div className="p-3 rounded-lg bg-[#00C853]/10 border border-[#00C853]/30 text-[#00C853] flex-shrink-0">
                                    <Mail size={20} />
                                </div>
                                <div className="space-y-1">
                                    <h3 className="font-heading font-bold text-white text-sm uppercase tracking-wider">
                                        E-mail Direct
                                    </h3>
                                    <a
                                        href="mailto:contact@pronospot.com"
                                        className="text-gray-300 hover:text-[#00C853] text-sm break-all transition-colors block"
                                    >
                                        contact@pronospot.com
                                    </a>
                                </div>
                            </div>

                            <div className="flex items-start gap-4">
                                <div className="p-3 rounded-lg bg-[#00C853]/10 border border-[#00C853]/30 text-[#00C853] flex-shrink-0">
                                    <Clock size={20} />
                                </div>
                                <div className="space-y-1">
                                    <h3 className="font-heading font-bold text-white text-sm uppercase tracking-wider">
                                        Délais de réponse
                                    </h3>
                                    <p className="text-gray-400 text-sm">
                                        Sous 24h à 48h maximum (hors week-ends et jours fériés).
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-start gap-4">
                                <div className="p-3 rounded-lg bg-[#00C853]/10 border border-[#00C853]/30 text-[#00C853] flex-shrink-0">
                                    <Shield size={20} />
                                </div>
                                <div className="space-y-1">
                                    <h3 className="font-heading font-bold text-white text-sm uppercase tracking-wider">
                                        Jeu Responsable
                                    </h3>
                                    <p className="text-gray-400 text-xs leading-relaxed">
                                        Notre équipe est formée aux questions de prévention et de jeu responsable.
                                        Vous pouvez nous solliciter pour toute question liée à l'exclusion ou aux ressources d'aide.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Additional info notice */}
                        <div className="p-4 rounded-xl bg-[#1E3A5F]/20 border border-[#2A4A6B]/50 text-center">
                            <p className="text-xs text-gray-400">
                                Vos données personnelles sont uniquement traitées pour apporter une réponse à votre demande, conformément à notre politique de confidentialité.
                            </p>
                        </div>
                    </aside>

                    {/* Contact form card */}
                    <div className="card-elev p-6 sm:p-8 border border-[#2A4A6B]">
                        <h2 className="font-heading text-2xl font-bold text-white mb-6 flex items-center gap-2">
                            <MessageSquare size={22} className="text-[#00C853]" />
                            Envoyer un message
                        </h2>

                        <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label htmlFor="name" className="text-xs font-semibold text-gray-300 uppercase tracking-wider">
                                        Nom complet
                                    </label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        placeholder="Jean Dupont"
                                        disabled={status === "loading"}
                                        className={`w-full bg-[#0D1B2A] border rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#00C853] focus:border-transparent transition-all ${
                                            errors.name ? "border-[#FF5252]" : "border-[#2A4A6B]"
                                        }`}
                                    />
                                    {errors.name && (
                                        <p className="text-xs text-[#FF5252] mt-0.5 flex items-center gap-1">
                                            <AlertCircle size={12} /> {errors.name}
                                        </p>
                                    )}
                                </div>

                                <div className="space-y-1.5">
                                    <label htmlFor="email" className="text-xs font-semibold text-gray-300 uppercase tracking-wider">
                                        Adresse e-mail
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="jean.dupont@email.com"
                                        disabled={status === "loading"}
                                        className={`w-full bg-[#0D1B2A] border rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#00C853] focus:border-transparent transition-all ${
                                            errors.email ? "border-[#FF5252]" : "border-[#2A4A6B]"
                                        }`}
                                    />
                                    {errors.email && (
                                        <p className="text-xs text-[#FF5252] mt-0.5 flex items-center gap-1">
                                            <AlertCircle size={12} /> {errors.email}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="space-y-1.5">
                                <label htmlFor="subject" className="text-xs font-semibold text-gray-300 uppercase tracking-wider">
                                    Sujet du message
                                </label>
                                <select
                                    id="subject"
                                    name="subject"
                                    value={formData.subject}
                                    onChange={handleChange}
                                    disabled={status === "loading"}
                                    className="w-full bg-[#0D1B2A] border border-[#2A4A6B] rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#00C853] focus:border-transparent transition-all"
                                >
                                    <option value="general">Question générale</option>
                                    <option value="partenariat">Partenariat & Publicité</option>
                                    <option value="bug">Signaler un problème technique</option>
                                    <option value="jeu-responsable">Jeu responsable / Protection</option>
                                    <option value="autre">Autre demande</option>
                                </select>
                            </div>

                            <div className="space-y-1.5">
                                <div className="flex justify-between items-center">
                                    <label htmlFor="message" className="text-xs font-semibold text-gray-300 uppercase tracking-wider">
                                        Votre message
                                    </label>
                                    <span className="text-[10px] text-gray-500">
                                        {formData.message.trim().length} caractères
                                    </span>
                                </div>
                                <textarea
                                    id="message"
                                    name="message"
                                    rows="5"
                                    value={formData.message}
                                    onChange={handleChange}
                                    placeholder="Décrivez votre demande en quelques lignes..."
                                    disabled={status === "loading"}
                                    className={`w-full bg-[#0D1B2A] border rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#00C853] focus:border-transparent transition-all resize-y min-h-[120px] ${
                                        errors.message ? "border-[#FF5252]" : "border-[#2A4A6B]"
                                    }`}
                                />
                                {errors.message && (
                                    <p className="text-xs text-[#FF5252] mt-0.5 flex items-center gap-1">
                                        <AlertCircle size={12} /> {errors.message}
                                    </p>
                                )}
                            </div>

                            {/* RGPD Consent */}
                            <div className="space-y-1.5">
                                <div className="flex items-start gap-2.5">
                                    <input
                                        type="checkbox"
                                        id="consent"
                                        name="consent"
                                        checked={formData.consent}
                                        onChange={handleChange}
                                        disabled={status === "loading"}
                                        className="mt-1 w-4 h-4 text-[#00C853] bg-[#0D1B2A] border-[#2A4A6B] rounded focus:ring-[#00C853] focus:ring-2"
                                    />
                                    <label htmlFor="consent" className="text-xs text-gray-400 leading-relaxed cursor-pointer select-none">
                                        J'accepte que mes données soient transmises et traitées uniquement pour répondre à ma demande, conformément à la Politique de Confidentialité.
                                    </label>
                                </div>
                                {errors.consent && (
                                    <p className="text-xs text-[#FF5252] mt-0.5 flex items-center gap-1">
                                        <AlertCircle size={12} /> {errors.consent}
                                    </p>
                                )}
                            </div>

                            <button
                                type="submit"
                                disabled={status === "loading"}
                                className="btn-green w-full py-3 px-5 text-sm font-semibold rounded-lg flex items-center justify-center gap-2 transition-all disabled:opacity-75 disabled:cursor-not-allowed"
                            >
                                {status === "loading" ? (
                                    <>
                                        <Loader2 size={16} className="animate-spin" />
                                        Envoi en cours...
                                    </>
                                ) : (
                                    <>
                                        <Send size={16} />
                                        Envoyer le message
                                    </>
                                )}
                            </button>

                            {/* Alert states */}
                            {status === "success" && (
                                <div
                                    className="flex items-start gap-3 p-4 rounded-lg bg-[#00C853]/10 border border-[#00C853]/30 text-xs text-[#00C853] animate-fade-in-up"
                                    data-testid="contact-success"
                                >
                                    <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" />
                                    <div>
                                        <strong className="font-heading font-bold block mb-1">Envoi réussi !</strong>
                                        {responseMsg}
                                    </div>
                                </div>
                            )}

                            {status === "error" && (
                                <div
                                    className="flex items-start gap-3 p-4 rounded-lg bg-[#FF5252]/10 border border-[#FF5252]/30 text-xs text-[#FF5252] animate-fade-in-up"
                                    data-testid="contact-error"
                                >
                                    <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                                    <div>
                                        <strong className="font-heading font-bold block mb-1">Erreur d'envoi</strong>
                                        {responseMsg}
                                    </div>
                                </div>
                            )}
                        </form>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default NousContacter;
